import { ExportOutlined } from '@ant-design/icons'
import { useRouter } from '@tanstack/react-router'
import { Button } from 'antd'
import { UnresolvedCommentBadge } from 'features/libra/comments/UnresolvedCommentBadge'
import { VersionPill } from 'features/libra/components/LifecyclePills'
import { CreateVersionModal } from 'features/libra/dashboard/CreateVersionModal'
import { getWorkflowPercComplete } from 'features/libra/helpers/getWorkflowPercComplete'
import {
  getDefaultStage,
  type LifecycleConfig,
} from 'features/libra/lifecycle/lifecycleTypes'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import type { EligibilityStatus } from 'features/libra/types/eligibility'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'
import type { FC } from 'react'
import { memo, useCallback, useMemo, useState } from 'react'
import { createLibraSearch } from 'routes/libra/-searchParams'
import styled from 'styled-components'
import { displayNameFromEmail } from 'utils/stringHelpers'

import { CreatorFilterButton } from './CreatorFilterButton'
import { TicketDetailModal } from './TicketDetailModal'

const RawBoardCardItem: FC<{
  ticket: LibraTicket
  workflow: LibraWorkflow | undefined
  onSelect: (ticket: LibraTicket) => void
  onOpen: (ticket: LibraTicket) => void
  unresolvedCount: number | undefined
}> = ({ ticket, workflow, onSelect, onOpen, unresolvedCount }) => {
  const pct = Math.round(getWorkflowPercComplete(ticket, workflow))
  const handleCardClick = useCallback(
    () => onSelect(ticket),
    [onSelect, ticket]
  )
  const handleOpenClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onOpen(ticket)
    },
    [onOpen, ticket]
  )
  return (
    <BoardCard onClick={handleCardClick}>
      <CardTitle>{ticket.title}</CardTitle>
      {ticket.createdBy && (
        <CardCreator>{displayNameFromEmail(ticket.createdBy)}</CardCreator>
      )}
      <CardFooter>
        <CardMeta>
          <VersionPill>v{ticket.version}</VersionPill>
          <UnresolvedCommentBadge count={unresolvedCount} />
          <PercentText>{pct}%</PercentText>
        </CardMeta>
        <Button
          size='small'
          type='primary'
          icon={<ExportOutlined />}
          onClick={handleOpenClick}>
          Open
        </Button>
      </CardFooter>
    </BoardCard>
  )
}
const BoardCardItem = memo(RawBoardCardItem)

interface LifecycleBoardViewProps {
  lifecycleConfig: LifecycleConfig
  tickets: ReadonlyArray<LibraTicket>
  allTickets: ReadonlyArray<LibraTicket>
  workflowMap: ReadonlyMap<string, LibraWorkflow>
  onTicketClick: (ticket: LibraTicket) => void
  onRefresh: () => Promise<void>
  filterText?: string
  selectedEligibility?: EligibilityStatus
  canCreateVersion: (ticket: LibraTicket) => boolean
  unresolvedCounts?: ReadonlyMap<string, number>
}

export const LifecycleBoardView: FC<LifecycleBoardViewProps> = ({
  lifecycleConfig,
  tickets,
  allTickets,
  workflowMap,
  onTicketClick,
  onRefresh,
  filterText = '',
  selectedEligibility,
  canCreateVersion,
  unresolvedCounts,
}) => {
  const [selectedTicket, setSelectedTicket] = useState<
    LibraTicket | undefined
  >()
  const [ticketForNewVersion, setTicketForNewVersion] = useState<
    LibraTicket | undefined
  >()
  const [creatorsByStage, setCreatorsByStage] = useState<
    Record<string, string[]>
  >({})
  const router = useRouter()

  const filteredTickets = useMemo(() => {
    let result = tickets
    const query = filterText.trim().toLowerCase()
    if (query) {
      result = result.filter(
        (t) =>
          t.title?.toLowerCase().includes(query) ||
          t.id.toLowerCase().includes(query)
      )
    }
    if (selectedEligibility) {
      result = result.filter((t) => {
        const val = unwrapFieldValue<string>(t.fieldValues?.eligibility)
        return val === selectedEligibility
      })
    }
    return result
  }, [tickets, filterText, selectedEligibility])

  const ticketsByStage = useMemo(() => {
    const map = new Map<string, LibraTicket[]>()
    for (const stage of lifecycleConfig.stages) {
      map.set(stage.id, [])
    }

    const defaultStageId = getDefaultStage(lifecycleConfig)?.id

    for (const ticket of filteredTickets) {
      const stageId = ticket.ticketStatus || ''
      if (map.has(stageId)) {
        map.get(stageId)!.push(ticket)
      } else if (defaultStageId && map.has(defaultStageId)) {
        map.get(defaultStageId)!.push(ticket)
      }
    }
    return map
  }, [lifecycleConfig, filteredTickets])

  const creatorEmailsByStage = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const [stageId, stageTickets] of ticketsByStage) {
      const emails = new Set<string>()
      for (const t of stageTickets) {
        if (t.createdBy) emails.add(t.createdBy)
      }
      map.set(stageId, [...emails].sort())
    }
    return map
  }, [ticketsByStage])

  const filteredTicketsByStage = useMemo(() => {
    const map = new Map<string, LibraTicket[]>()
    for (const [stageId, stageTickets] of ticketsByStage) {
      const creators = creatorsByStage[stageId]
      if (creators && creators.length > 0) {
        map.set(
          stageId,
          stageTickets.filter(
            (t) => t.createdBy !== null && creators.includes(t.createdBy)
          )
        )
      } else {
        map.set(stageId, stageTickets)
      }
    }
    return map
  }, [ticketsByStage, creatorsByStage])

  const handleCreatorsChange = useCallback(
    (emails: string[], stageId?: string) => {
      if (!stageId) return
      setCreatorsByStage((prev) => ({ ...prev, [stageId]: emails }))
    },
    []
  )

  const handleCloseDetail = useCallback(() => setSelectedTicket(undefined), [])

  const handleOpenTicketFromDetail = useCallback(
    (t: LibraTicket) => {
      setSelectedTicket(undefined)
      onTicketClick(t)
    },
    [onTicketClick]
  )

  const handleRequestNewVersion = useCallback(
    (t: LibraTicket) => setTicketForNewVersion(t),
    []
  )

  const handleVersionCreated = useCallback(
    async (newTicketId: string, sourceTicket: LibraTicket) => {
      const orgId = sourceTicket.tags.organization as string | undefined
      const workflowId = sourceTicket.workflowId
      if (!orgId || !workflowId) return

      try {
        await onRefresh()
      } catch {
        // refresh is best-effort before navigation
      }
      router.navigate({
        to: '/libra',
        search: createLibraSearch({
          orgId,
          workflowId,
          ticket: newTicketId,
          thread: undefined,
        }),
      })
      setTicketForNewVersion(undefined)
      setSelectedTicket(undefined)
    },
    [onRefresh, router]
  )

  return (
    <>
      <BoardContainer>
        {lifecycleConfig.stages.map((stage) => {
          const stageTickets = filteredTicketsByStage.get(stage.id) ?? []
          const stageEmails = creatorEmailsByStage.get(stage.id) ?? []
          return (
            <Column key={stage.id}>
              <ColumnHeader $color={stage.color}>
                <ColumnHeaderTopRow>
                  <ColumnName>{stage.name}</ColumnName>
                  <ColumnHeaderRight>
                    <CreatorFilterButton
                      emails={stageEmails}
                      selectedEmails={creatorsByStage[stage.id] ?? []}
                      onChange={handleCreatorsChange}
                      id={stage.id}
                      compact
                    />
                    <ColumnCount>{stageTickets.length}</ColumnCount>
                  </ColumnHeaderRight>
                </ColumnHeaderTopRow>
              </ColumnHeader>
              <ColumnBody>
                {stageTickets.map((ticket) => (
                  <BoardCardItem
                    key={ticket.id}
                    ticket={ticket}
                    workflow={workflowMap.get(ticket.workflowId)}
                    onSelect={setSelectedTicket}
                    onOpen={onTicketClick}
                    unresolvedCount={unresolvedCounts?.get(ticket.id)}
                  />
                ))}
                {stageTickets.length === 0 && (
                  <EmptyColumn>No tickets</EmptyColumn>
                )}
              </ColumnBody>
            </Column>
          )
        })}
      </BoardContainer>

      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          allTickets={allTickets}
          workflow={workflowMap.get(selectedTicket.workflowId)}
          lifecycleConfig={lifecycleConfig}
          onClose={handleCloseDetail}
          onTicketClick={handleOpenTicketFromDetail}
          onCreateVersion={handleRequestNewVersion}
          canCreateVersion={canCreateVersion(selectedTicket)}
        />
      )}

      <CreateVersionModal
        isOpen={Boolean(ticketForNewVersion)}
        sourceTicket={ticketForNewVersion}
        onClose={() => setTicketForNewVersion(undefined)}
        onSuccess={handleVersionCreated}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Styled Components — Board
// ---------------------------------------------------------------------------

const BoardContainer = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 0 0 12px;
  min-height: 300px;
`

const Column = styled.div`
  flex: 1 0 0;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray[700]};
  background: ${({ theme }) => theme.colors.gray[800]};
`

const ColumnHeader = styled.div<{ $color?: string }>`
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  border-bottom: 2px solid ${({ $color }) => $color ?? '#666'};
  flex-shrink: 0;
`

const ColumnHeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const ColumnHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const ColumnName = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
`

const ColumnCount = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.secondaryText};
  background: ${({ theme }) => theme.colors.gray[700]};
  border-radius: 999px;
  padding: 2px 7px;
`

const ColumnBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const BoardCard = styled.div`
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray[700]};
  background: ${({ theme }) => theme.colors.gray[800]};
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.forecast.primary};
  }
`

const CardTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 4px;
`

const CardCreator = styled.div`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 6px;
`

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const EmptyColumn = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-align: center;
  padding: 24px 0;
`

const PercentText = styled.span`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`
